<template>
  <div
    :class="[
      useStore ? 'use-store' : 'no-store',
    ]"
    class="block-filter"
  >
    <h2 class="block-filter__title">
      {{ title }}
    </h2>
    <div
      class="block-filter__select-wrapper"
      :class="`amount-of-dropdowns-${amountOfDropdowns}`"
      :style="`--amount-of-dropdowns: ${amountOfDropdowns}`"
    >
      <div
        v-if="showPrefilterReset && !useStore"
        class="block-filter__prefilter-reset"
        @click="resetQuery"
        @keypress="resetQuery"
      >
        {{ prefilterResetText }}
        <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" class="block-filter__prefilter-reset-icon">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M3.92961 3.92927C4.32014 3.53875 4.9533 3.53875 5.34383 3.92927L18.0717 16.6572C18.4623 17.0477 18.4623 17.6809 18.0717 18.0714C17.6812 18.4619 17.0481 18.4619 16.6575 18.0714L3.92961 5.34349C3.53909 4.95296 3.53909 4.3198 3.92961 3.92927Z" fill="#486A73" />
          <path fill-rule="evenodd" clip-rule="evenodd" d="M3.92976 18.0719C3.53924 17.6813 3.53924 17.0482 3.92976 16.6576L16.6577 3.92972C17.0482 3.53919 17.6814 3.53919 18.0719 3.92972C18.4624 4.32024 18.4624 4.95341 18.0719 5.34393L5.34398 18.0719C4.95345 18.4624 4.32029 18.4624 3.92976 18.0719Z" fill="#486A73" />
        </svg>
      </div>

      <template
        v-for="dropdown, index in dropdowns"
        :key="dropdown.uid"
      >
        <AtomSingleselect
          v-if="dropdown.field_type === 'single_option'
            || dropdown.label === 'Datum'
            || dropdown.label === 'Date'
            || dropdown.label === 'Data'"
          :key="dropdown.name"
          :name="dropdown.label"
          :label="dropdown.label"
          :type="dropdown.type"
          :options="[...dropdown.options.map((option) => ({
            ...option,
            label: option.name,
            value: option.value,
            language: option.name,
          }))]"
          :initial-value="getInitialValues(dropdown)"
          :card-index="index"
          class="block-filter__select"
          @set-data="setFilter"
        />

        <AtomMultiselect
          v-else
          :key="dropdown.label"
          :options="dropdown.options"
          :label="dropdown.label"
          class="block-filter__select"
          :initial-values="getInitialValues(dropdown)"
          @set-data="setFilter"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-nested-ternary */
const props = defineProps({
    title: {
        type: String,
        default: '',
    },

    dropdowns: {
        type: Array,
        default: () => [],
    },

    color: {
        type: String,
        default: '',
    },

    useStore: {
        type: Boolean,
        default: true,
    },

    showPrefilterReset: {
        type: Boolean,
        default: false,
    },

    prefilterResetText: {
        type: String,
        default: '',
    },
});

const filterStore = useFilterStore();
const emit = defineEmits(['on-set-filter']);
const { query } = useRoute();
const router = useRouter();
const route = useRoute();

const amountOfDropdowns = computed(() => {
    const dropdownsLength = props.dropdowns.length;
    const total = props.showPrefilterReset ? dropdownsLength + 1 : dropdownsLength;
    return total;
});

const getInitialValues = (dropdown) => {
    switch (dropdown.name) {
    case 'type-of-article':
        return isArray(query.types)
            ? query.types
            : isStringNotEmpty(query.types)
                ? [query.types]
                : null;
    case 'date':
        return isArrayNotEmpty(query.years)
            ? query.years[0]
            : query.years || null;
    case 'countries':
        return isArray(query.countries)
            ? query.countries
            : isStringNotEmpty(query.countries)
                ? [query.countries]
                : null;
    case 'type-of-activity':
        return isArray(query.types)
            ? query.types
            : isStringNotEmpty(query.types)
                ? [query.types]
                : null;
    case 'department':
        return isArray(query.departments)
            ? query.departments
            : isStringNotEmpty(query.departments)
                ? [query.departments]
                : null;
    case 'thema':
        return isArray(query.themas)
            ? query.themas
            : isStringNotEmpty(query.themas)
                ? [query.themas]
                : null;
    default:
        break;
    }

    return '';
};

const queryMutator = (data, type) => {
    const newQuery = { ...route.query };

    if (typeof query[type] === 'string' && isArrayNotEmpty(data.uuids)) {
        const uuid = data.uuids[0];
        newQuery[type] = uuid;
    }

    if (isArrayNotEmpty(data.uuids)) {
        newQuery[type] = data.uuids;
    }

    if (!isArrayNotEmpty(data.uuids)) {
        delete newQuery[type];
    }

    return newQuery;
};

const setFilter = (data) => {
    if (props.useStore) {
        emit('on-set-filter', data.uuids);

        switch (data.type) {
        case 'countries':
            filterStore.setCountries(data.uuids);
            break;
        case 'date':
            filterStore.setYears(data.uuids);
            break;
        case 'type-of-article':
            filterStore.setTypesOfArticle(data.uuids);
            break;
        case 'type-of-activity':
            filterStore.setTypesOfActivity(data.uuids);
            break;
        case 'type-of-project':
            filterStore.setTypeOfProject(data.uuids);
            break;
        case 'canton':
            filterStore.setCanton(data.uuids);
            break;
        case 'sort-title':
            filterStore.setSortByName(data.uuids);
            break;
        case 'sort-tCO2e':
            filterStore.setSortBytCO2e(data.uuids);
            break;
        case 'department':
            filterStore.setDepartments(data.uuids);
            break;
        case 'division':
            filterStore.setDivision(data.uuids);
            break;
        case 'thema':
            filterStore.setThemas(data.uuids);
            break;
        default:
            break;
        }
    } else {
        let newQuery = { ...route.query };

        switch (data.type) {
        case 'countries':
            newQuery = queryMutator(data, 'countries');
            break;
        case 'date':
            if (isArrayNotEmpty(data.uuids)) {
                const newUid = data.uuids[0];
                newQuery.years = newUid;
            } else {
                delete newQuery.years;
            }
            break;
        case 'type-of-article':
        case 'type-of-activity':
        case 'type-of-project':
            newQuery = queryMutator(data, 'types');
            break;
        case 'canton':
            newQuery = queryMutator(data, 'canton');
            break;
        case 'department':
            newQuery = queryMutator(data, 'departments');
            break;
        case 'thema':
            newQuery = queryMutator(data, 'themas');
            break;
        case 'division':
            // TODO: Check if this is still needed
            newQuery = queryMutator(data, 'division');
            break;
        case 'sort-title':
            // TODO: Check if this is still needed
            // filterStore.setSortByName(data.uuids);
            break;
        case 'sort-tCO2e':
            // TODO: Check if this is still needed
            // filterStore.setSortBytCO2e(data.uuids);
            break;
        default:
            break;
        }

        router.replace({ query: newQuery });
    }
};

const colorTopLevel = inject('colorSchema');
const color1 = computed(() => `var(--c-${colorTopLevel.value}-1)`);
const color11 = computed(() => `var(--c-${colorTopLevel.value}-11)`);

const resetQuery = () => {
    router.replace({ query: {} });
};
</script>

<style lang="scss">
.block-filter {
    display: flex;
    justify-content: space-between;
    padding: 20px 29px;
    padding-top: 22px;
    margin-bottom: 5px;
    background-color: var(--c-white);
    grid-column: 3 / span 14;

    @include tablet {
        grid-column: 2 / span 16;
    }

    @include mobile {
        flex-direction: column;
        align-items: unset;
        padding: 10px;
        .storyblok-projects-block & {
            padding: 20px;
        }
    }
    .storyblok-employees-block & {
        padding: 20px 31px;
    }
}
.block-filter__select-wrapper {
    .no-store & {
        display: grid;
        gap: 10px;
        grid-template-columns: repeat(var(--amount-of-dropdowns), 1fr);

        @include mq(1570px) {
            &.amount-of-dropdowns-4 {
                grid-template-columns: repeat(2, 1fr);
            }
        }

        @include tablet-portrait {
            grid-template-columns: repeat(2, 1fr);

            &.amount-of-dropdowns-3 {
                grid-template-columns: repeat(3, 1fr);
            }
        }

        @include mobile {
            grid-template-columns: repeat(1, 1fr);
            row-gap: 5px;

            &.amount-of-dropdowns-4 {
                grid-template-columns: repeat(1, 1fr);
            }

            &.amount-of-dropdowns-3 {
                grid-template-columns: repeat(1, 1fr);
            }
        }
    }

    .use-store & {
        display: flex;
        justify-content: space-between;
        margin-top: auto;
        margin-bottom: auto;

        .storyblok-projects-block__filter & {
            flex-wrap: wrap;
            justify-content: flex-end;
            row-gap: 5px;
        }

        @include mobile {
            flex-direction: column;
            row-gap: 5px;
        }
    }
}

.block-filter__title {
    padding-right: 20px;
    color: v-bind(color1);
    font-family: var(--f-family--regular);
    font-size: var(--f-size--h3);
    line-height: var(--l-height--h3);

    @include mobile {
        margin-bottom: 10px;
    }
}

.block-filter__select {
    .use-store & {
        margin-left: 10px;
    }

    @include mobile {
        margin-left: 0;
    }

    > button {
        width: 167px;
        padding-top: 8px;
        padding-right: 10px;
        padding-bottom: 5px;
        background: v-bind(color11);
        font-size: 14px;
        line-height: 18px;
        text-align: right;

        @include mobile {
            width: 100%;
            margin-top: 5px;
        }
    }

    svg {
        margin-left: 5px;
        font-size: 10px;
        transform: translateY(-1px);
    }
    .storyblok-projects-block__filter & {
        width: 167px;
    }
}
.block-filter__select-category {
    display: none;
}

.block-filter__prefilter-reset {
    display: flex;
    min-width: 167px;
    height: 30px;
    align-items: center;
    justify-content: flex-end;
    padding: 0px 10px;
    padding-top: 3px;
    background-color: v-bind(color1);
    color: var(--c-white);
    cursor: pointer;
    font-family: var(--f-family--thin);
    font-size: var(--f-size--tag);
}

.block-filter__prefilter-reset-icon {
    @include z-index('selectLabel');

    width: 14px;
    height: 14px;
    margin-top: -4px;
    margin-left: 10px;

    path {
        fill: var(--c-white) !important;
    }
}
</style>
